import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import SettingsEntryPoint from '@databox/ember-integrations/lib/components/Ai/Settings/SettingsEntryPoint';
import type { SettingsEntryPointProps } from '@databox/ember-integrations/lib/components/Ai/Settings/SettingsEntryPoint';
import type { SkillsTab } from '@databox/types/domains/ai/settings.enums';
import type { ProfileAnchor, SettingsTab } from '@databox/types/domains/settings/settings.enums';
import type { UserProfile } from '@databox/types/domains/user/profile.types';

import type { ReactIntegrations, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { getShadowDomMode } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

interface SettingsModalSignature {
	Element: HTMLDivElement;
	Args: {
		activeTab: SettingsTab;
		onTabChange: (tab: SettingsTab) => void;
		anchor: ProfileAnchor | null;
		onAnchorReached: () => void;
	};
}

export default class GenieSettingsModalComponent extends Component<SettingsModalSignature> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('react') declare reactService: Service.React;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	// Last section and skills tab applied to the React mount; compared on update so we re-render
	// only on an actual change. Seeded in `setupView`, which always runs before the first update.
	private currentActiveTab!: SettingsTab;
	private currentSkillsTab: SkillsTab | null = null;

	reactElement: ReactIntegrations.Root = null;

	@tracked private isAppReady = false;

	// The Connectors tab shows the mcp coming-soon teaser while the feature is gated; its waitlist is
	// the same user setting the Data Manager teaser used, so existing signups carry over.
	get isOnConnectorsWaitlist(): boolean {
		return !!this.userSettingsService.userSettings[
			UserSettingsKey.COMING_SOON_WAITLIST_MCP_CONNECTORS
		];
	}

	get isProfileEnabled(): boolean {
		return !this.currentUserService.isDemo;
	}

	get isCompanyEnabled(): boolean {
		return this.isProfileEnabled && this.currentUserService.isAdmin;
	}

	@action
	setupView(node: HTMLElement) {
		this.currentActiveTab = this.args.activeTab;
		this.currentSkillsTab = this.aiChatService.settingsSkillsTab;

		mountReactRoot(this, node, () => this.renderSettings());

		void this.waitForApp();
	}

	private async waitForApp(): Promise<void> {
		await this.navigationService.initialRequestsPromise;

		if (this.isDestroying || this.isDestroyed) {
			return;
		}

		this.isAppReady = true;
		this.renderSettings();
	}

	@action
	teardownView() {
		unmountReactRoot(this);
	}

	@action
	onNavigationChange() {
		const skillsTab = this.aiChatService.settingsSkillsTab;

		if (this.currentActiveTab === this.args.activeTab && this.currentSkillsTab === skillsTab) {
			return;
		}

		this.currentActiveTab = this.args.activeTab;
		this.currentSkillsTab = skillsTab;
		this.renderSettings();
	}

	@action
	joinConnectorsWaitlist() {
		if (this.isOnConnectorsWaitlist) {
			return;
		}

		void this.userSettingsService.save(
			UserSettingsKey.COMING_SOON_WAITLIST_MCP_CONNECTORS,
			true,
		);
		this.analyticsService.event(TrackingEvent.COMING_SOON_WAITLIST_MCP_CONNECTORS);

		// Re-render so the updated waitlist state reaches the mounted React tree.
		this.renderSettings();
	}

	// White-labeling and the AI-agent visibility only settle once billing data resolves.
	@action
	onAccessChange() {
		this.renderSettings();
	}

	@action
	onManageNotifications() {
		this.aiChatService.closeSettingsAndGoTo('settings.notifications');
	}

	@action
	onLoggedOut() {
		this.navigationService.logout('Password changed');
	}

	@action
	onOpenSupport() {
		this.currentUserService.chatWithSupport();
	}

	@action
	onAccountDeleted() {
		this.navigationService.logout('Account deleted');
	}

	@action
	onTokenRefreshRequired() {
		void this.currentUserService.refreshTokensNow();
	}

	@action
	onProfileUpdated(profile: UserProfile) {
		this.currentUserService.applyProfile(profile);
	}

	@action
	onAnchorChange() {
		this.renderSettings();
	}

	@action
	onSkillsTabChange(skillsTab: SkillsTab) {
		this.aiChatService.settingsSkillsTab = skillsTab;
	}

	@action
	onStartChat(...args: Parameters<Service.AIChat['onStartChat']>) {
		this.aiChatService.closeSettings();
		this.aiChatService.onStartChat(...args);
	}

	private renderSettings() {
		if (!this.isAppReady) {
			return;
		}

		const isAiEnabled = this.aiChatService.isAiChatAvailable;

		if (!this.isProfileEnabled && !isAiEnabled) {
			this.aiChatService.closeSettings();

			return;
		}

		const props: SettingsEntryPointProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			spaceId: this.accountService.id,
			userId: Number(this.currentUserService.id),
			userLevel: this.currentUserService.model?.level,
			onBack: this.aiChatService.closeSettings,
			activeTab: this.args.activeTab,
			onTabChange: this.args.onTabChange,
			activeSkillsTab: this.aiChatService.settingsSkillsTab ?? undefined,
			onSkillsTabChange: this.onSkillsTabChange,
			isProfileEnabled: this.isProfileEnabled,
			isCompanyEnabled: this.isCompanyEnabled,
			onOpenSupport: this.onOpenSupport,
			onAccountDeleted: this.onAccountDeleted,
			isAiEnabled,
			onAddCredits: this.aiChatService.onAddCredits,
			onStartChat: this.onStartChat,
			onUpgrade: this.aiChatService.onUpgrade,
			hasAccessToMarketplace:
				!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins,
			canDownloadSkills: !this.navigationService.isMobileApp,
			showToast: this.aiChatService.showToast,
			onLoggedOut: this.onLoggedOut,
			onTokenRefreshRequired: this.onTokenRefreshRequired,
			onConnectSlack: this.aiChatService.connectSlack,
			onManageNotifications:
				this.navigationService.isMobileApp || this.currentUserService.isViewer
					? undefined
					: this.onManageNotifications,
			isWhiteLabeled: this.accountService.whiteLabelingEnabled,
			anchor: this.args.anchor ?? undefined,
			onAnchorReached: this.args.onAnchorReached,
			onProfileUpdated: this.onProfileUpdated,
			connectorsWaitlist: {
				isJoined: this.isOnConnectorsWaitlist,
				onJoin: this.joinConnectorsWaitlist,
			},
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				SettingsEntryPoint(props),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{didUpdate this.onNavigationChange @activeTab this.aiChatService.settingsSkillsTab}}
			{{didUpdate this.onAnchorChange @anchor}}
			{{didUpdate
				this.onAccessChange
				this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
				this.accountService.whiteLabelingEnabled
				this.aiChatService.isAiChatAvailable
			}}
			{{willDestroy this.teardownView}}
			id="settings-modal-container"
			...attributes
		/>
	</template>
}
